import { loadAvailability, loadReportPublication } from '../../components/Publications/publicationApi';
import { preserveHistoricalProperty } from '../../components/Publications/publicationModel';
// src/pages/Home/Home.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo  } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Map from '../../components/Map/Map';
import Navbar from '../../components/Navbar/Navbar';
import PropertyList from '../../components/PropertyList/PropertyList';
import SearchExpansionNotice from '../../components/PropertyList/SearchExpansionNotice';
import { expandPolygonPaths } from '../../components/Map/utils/polygonExpansion';
import { useProperties, transformProperty } from '../../hooks/useProperties';
import { useSearchFilters } from '../../hooks/useSearchFilters';
import '../../styles/Home.css';
import PropertyWizard from '../../components/PropertyWizard';
import { validateToken, getSession, updateSession } from '../../api/propertiesApi';
import {
  WIZARD_CHARACTERISTICS,
  buildFilterBoundsFromWizard,
  mapSessionCharacteristicFields,
} from '../../utils/characteristicFields';

const comparableKey = property => property.property_group_key || `id:${property.id}`;

export default function Home() {
  const location = useLocation();
  const navigate = useNavigate();

  // 👉 Sólo mostrar wizard si venís con propertyData y NO hay sesión creada
  const initialHasSession = Boolean(sessionStorage.getItem('current_session'));
  const [showWizard, setShowWizard] = useState(Boolean(location.state?.propertyData) && !initialHasSession);
  const [wizardData, setWizardData] = useState(location.state?.propertyData || null);
  const [savedComparables, setSavedComparables] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const snapshotRequest = useRef(0);
  const [publicationChoices, setPublicationChoices] = useState({});
  const [selectionPending, setSelectionPending] = useState(false);
  const publicationChoiceEpoch = useRef(0);

  const { filters, setFilters } = useSearchFilters();
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  // "Inactivas" por defecto: el usuario activa las que quiere incluir en el informe.
  // Siguen visibles en lista y mapa con aspecto normal; solo el switch indica el estado.
  const [inactiveProperties, setInactiveProperties] = useState([]);
  const seenPropertyIdsRef = useRef(new Set());
  const [visitedProperties, setVisitedProperties] = useState([]);
  // Modo de búsqueda activo y polígonos dibujados. Los polígonos se guardan
  // como paths planos (no instancias de google.maps.Polygon) para poder
  // serializarlos en la sesión.
  const [searchMode, setSearchMode] = useState('radius'); // 'radius' | 'polygons'
  const [polygonsPaths, setPolygonsPaths] = useState([]); // Always the original drawing.
  const [polygonExpansion, setPolygonExpansion] = useState(0);
  const expandedZone = useMemo(() => {
    try {
      return { paths: expandPolygonPaths(polygonsPaths, polygonExpansion), error: null };
    } catch {
      return { paths: polygonsPaths, error: "No se pudo ampliar tanto esta zona. Se conserva el dibujo original; probá una ampliación menor." };
    }
  }, [polygonsPaths, polygonExpansion]);

  // Boot flags
  const [booting, setBooting] = useState(true);
  const [listOpen, setListOpen] = useState(true);
  const [filtersRequest, setFiltersRequest] = useState(0);
  const [firstLoadDone, setFirstLoadDone] = useState(false);

  // En modo polygons la búsqueda sólo tiene sentido si hay al menos una zona
  // dibujada; si no, evitamos pegarle al backend sin área.
  const hasGeoArea =
    searchMode === 'polygons' ? polygonsPaths.length > 0 : Boolean(filters?.center);
  const enabled =
    !booting && filters && Object.keys(filters).length > 0 && hasGeoArea;

  // El hook sólo pegará a la API si hay filtros (enabled)
  const searchPolygons = useMemo(
    () => (searchMode === 'polygons' ? expandedZone.paths : []),
    [searchMode, expandedZone.paths],
  );
  const { properties: liveProperties, loading, refreshing, error } = useProperties(
    filters,
    searchPolygons,
    { enabled: enabled && savedComparables === null }
  );
  const properties = useMemo(() => savedComparables ?? liveProperties.map(property =>
    publicationChoices[comparableKey(property)] || property
  ), [savedComparables, liveProperties, publicationChoices]);

  // Si cierra el aviso de pocos resultados, vuelve a aparecer al cambiar los filtros.
  const [widenDismissed, setWidenDismissed] = useState(false);
  useEffect(() => { setWidenDismissed(false); }, [filters, searchMode, polygonExpansion]);

  const fewResults = enabled && !error && properties.length < 10;
  const [displayedProps, setDisplayedProps] = useState([]);

  // ---------- helpers de sesión ----------
  const extractFiltersFromSession = (sessionData) => {
    if (!sessionData) return null;
    let f =
      sessionData.filters ??
      sessionData.session_data ??
      sessionData.data ??
      sessionData.payload ??
      null;

    if (typeof f === 'string') {
      try { f = JSON.parse(f); } catch {/* ignore */ }
    }
    if (f && f.filters) f = f.filters;
    return f || null;
  };

  const mapSessionToWizard = (s) => {
    if (!s || typeof s !== 'object') return s;

    const isSessionShape =
      s.operationType !== undefined ||
      s.typeProperty !== undefined ||
      (s.amenities && typeof s.amenities === 'object');

    if (!isSessionShape) {
      return mapSessionCharacteristicFields(s, { ...s });
    }

    const result = {
      type_operation: s.operationType,
      type_property: s.typeProperty,
      parking: s.parking != null ? Number(s.parking) : undefined,

      has_pool: !!s.amenities?.Pileta,
      has_grill: !!s.amenities?.Parrilla,
      has_garden: !!s.amenities?.Jardín,
      has_terrace: !!s.amenities?.Terraza,
      has_balcony: !!s.amenities?.Balcón,

      selectedPlace: s.selectedPlace
        ? {
          lat: Number(s.selectedPlace.lat),
          lng: Number(s.selectedPlace.lng),
          formatted_address: s.selectedPlace.formatted_address || "",
        }
        : undefined,

      radius: s.radius != null ? Math.min(1500, Math.max(200, Number(s.radius))) : 500,
      search_mode: s.search_mode || s.searchMode || 'radius',
      polygons: Array.isArray(s.polygons) ? s.polygons : [],
    };

    return mapSessionCharacteristicFields(s, result);
  };

  const applyFiltersFromWizardData = useCallback((wd) => {
    const characteristicFilters = WIZARD_CHARACTERISTICS.reduce((acc, config) => ({
      ...acc,
      ...buildFilterBoundsFromWizard(wd, config),
    }), {});

    const wizardFilters = {
      transactionType: wd.type_operation || 'Venta',
      typeProperty: wd.type_property || '',
      searchValue: wd.selectedPlace?.formatted_address || '',
      ...characteristicFilters,
      minParking: wd.parking ? Math.max(1, wd.parking) : null,
      hasPool: wd.has_pool || false,
      hasGrill: wd.has_grill || false,
      hasGarden: wd.has_garden || false,
      hasTerrace: wd.has_terrace || false,
      hasBalcony: wd.has_balcony || false,
      center: wd.selectedPlace
        ? { lat: wd.selectedPlace.lat, lng: wd.selectedPlace.lng }
        : null,
      radius: wd.radius != null ? Math.min(1500, Math.max(200, Number(wd.radius))) : 500,
    };

    setFilters(wizardFilters);
    setSelectedPlace(wd.selectedPlace || null);

    // Restaurar modo de búsqueda + polígonos persistidos
    const mode = wd.search_mode === 'polygons' ? 'polygons' : 'radius';
    setSearchMode(mode);
    setPolygonExpansion(0);
    setPolygonsPaths(Array.isArray(wd.polygons) ? wd.polygons : []);
  }, [setFilters]);

  // ---------- Reanudación / creación ----------
  useEffect(() => {
    setPublicationChoices({});
    setSelectionPending(false);
    publicationChoiceEpoch.current += 1;
    (async () => {
      const resumeSid = sessionStorage.getItem('resume_session');

      // A) Link con resume_session
      if (resumeSid && !location.state?.propertyData) {
        const ok = await validateToken().catch(() => false);
        if (!ok) { setBooting(false); return; }

        try {
          const sessionData = await getSession(resumeSid);
          const extracted = extractFiltersFromSession(sessionData);
          const snapshot = extracted?.comparable_snapshot?.properties;
          if (Array.isArray(snapshot)) {
            setSavedComparables(snapshot.map(p => preserveHistoricalProperty(p, null)));
            setInactiveProperties([]);
            seenPropertyIdsRef.current = new Set(snapshot.map(p => p.id));
            setCheckingAvailability(true);
            const request = ++snapshotRequest.current;
            loadAvailability(snapshot.map(p => p.id)).then(current => {
              if (request === snapshotRequest.current) setSavedComparables(snapshot.map(p => preserveHistoricalProperty(p, current[p.id])));
            }).catch(() => {}).finally(() => { if (request === snapshotRequest.current) setCheckingAvailability(false); });
          }
          setWizardData({ ...(extracted || {}), session_id: Number(resumeSid) });

          sessionStorage.setItem('current_session', String(resumeSid));
          setShowWizard(false);
        } catch (e) {
          console.error("Error fetching session (resume):", e);
        } finally {
          sessionStorage.removeItem('resume_session');
        }
        return; // boot baja cuando apliquemos wizardData
      }

      // B) Ya hay current_session (se creó recién o ya existía)
      const current = sessionStorage.getItem('current_session');
      if (current && !location.state?.propertyData) {
        try {
          const sessionData = await getSession(current);
          const extracted = extractFiltersFromSession(sessionData);
          const snapshot = extracted?.comparable_snapshot?.properties;
          if (Array.isArray(snapshot)) {
            setSavedComparables(snapshot.map(p => preserveHistoricalProperty(p, null)));
            setInactiveProperties([]);
            seenPropertyIdsRef.current = new Set(snapshot.map(p => p.id));
            setCheckingAvailability(true);
            const request = ++snapshotRequest.current;
            loadAvailability(snapshot.map(p => p.id)).then(current => {
              if (request === snapshotRequest.current) setSavedComparables(snapshot.map(p => preserveHistoricalProperty(p, current[p.id])));
            }).catch(() => {}).finally(() => { if (request === snapshotRequest.current) setCheckingAvailability(false); });
          }
          setWizardData({ ...(extracted || {}), session_id: Number(current) });
          setShowWizard(false);
        } catch (e) {
          console.error("Error fetching session (current):", e);
          setShowWizard(true);
          setBooting(false);
        }
        return;
      }

      // C) Venís con propertyData (creación). Si YA hay sesión, ocultá wizard.
      if (location.state?.propertyData) {
        const haveSessionNow = Boolean(sessionStorage.getItem('current_session'));
        setWizardData({
          ...location.state.propertyData,
          // fallback por si el guard ya creó la sesión y la dejó en sessionStorage
          session_id: Number(sessionStorage.getItem('current_session')) || undefined,
        });
        setShowWizard(!haveSessionNow); // 👈 si ya existe current_session, NO lo muestres
        return;
      }

      setBooting(false);
    })();
    return () => { snapshotRequest.current += 1; publicationChoiceEpoch.current += 1; };
  }, [location.state]);

  // Cuando ya exista current_session + venías con propertyData, limpiá el state para que no re-aparezca el wizard
  useEffect(() => {
    const haveSession = Boolean(sessionStorage.getItem('current_session'));
    if (haveSession && location.state?.propertyData) {
      navigate('/home', { replace: true, state: null }); // limpia el state
    }
  }, [location.state, navigate]);

  // ---------- Aplicar wizardData y cerrar boot ----------
  useEffect(() => {
    if (!wizardData) return;
    const wd = mapSessionToWizard(wizardData);
    applyFiltersFromWizardData(wd);
    setShowWizard(false);             // 👈 siempre ocultar tras aplicar filtros
    setBooting(false);
  }, [wizardData, applyFiltersFromWizardData]);

  // ---------- Put properties_count (debounced) ----------
  const lastCountRef = useRef(null);
  const putTimerRef = useRef(null);
  useEffect(() => {
    const sid = sessionStorage.getItem('current_session');
    if (!sid || booting || savedComparables !== null) return;

    const count = properties?.length ?? 0;
    if (count === lastCountRef.current) return;
    lastCountRef.current = count;

    clearTimeout(putTimerRef.current);
    putTimerRef.current = setTimeout(() => {
      updateSession(sid, { properties_count: count }).catch(console.error);
    }, 700);
    return () => clearTimeout(putTimerRef.current);
  }, [properties?.length, savedComparables, booting]);

  // ---------- Derivados UI ----------
  // Las inactivas siguen visibles; el PropertyReport filtra al generar.
  useEffect(() => {
    setDisplayedProps(properties);
  }, [properties]);

  // Propiedades nuevas arrancan inactivas; las que el usuario ya activó se mantienen.
  useEffect(() => {
    if (!properties?.length) {
      setInactiveProperties([]);
      seenPropertyIdsRef.current = new Set();
      return;
    }

    setInactiveProperties((prev) => {
      const prevInactive = new Set(prev);
      const nextInactive = [];

      for (const { id } of properties) {
        const isNew = !seenPropertyIdsRef.current.has(id);
        seenPropertyIdsRef.current.add(id);

        if (isNew || prevInactive.has(id)) {
          nextInactive.push(id);
        }
      }

      return nextInactive;
    });
  }, [properties]);

  useEffect(() => {
    if (enabled && !loading && !firstLoadDone) setFirstLoadDone(true);
  }, [enabled, loading, firstLoadDone]);

  // ---------- Handlers ----------
  const handleWizardComplete = (newWizardData) => {
    setWizardData(newWizardData);
    setShowWizard(false);
  };
  // Toggle: si está activa la marca como inactiva, y viceversa. Sin lista
  // negra permanente — el switch del UI controla el estado en ambos sentidos.
  const handleToggleInactive = (id) => setInactiveProperties((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );
  const handleSelectPublication = async (property, publication) => {
    if (selectionPending || property.is_historical || savedComparables !== null) return;
    const epoch = publicationChoiceEpoch.current;
    setSelectionPending(true);
    try {
      // Fetch all fields together: never combine one advertiser's price with
      // another advertiser's photo, dimensions, expenses or URL.
      const result = await loadReportPublication(property.id, publication.property_id);
      if (epoch !== publicationChoiceEpoch.current) return;
      if (result.property_group_key !== property.property_group_key || result.id !== publication.property_id) {
        throw new Error('El anuncio ya no pertenece a esta propiedad. Actualizá la búsqueda.');
      }
      const chosen = { ...transformProperty(result), report_publication_selected: true };
      seenPropertyIdsRef.current.add(chosen.id);
      setInactiveProperties(previous => previous.map(id => id === property.id ? chosen.id : id));
      setVisitedProperties(previous => [...new Set(previous.map(id => id === property.id ? chosen.id : id))]);
      setSelectedProperty(previous => previous && comparableKey(previous) === comparableKey(property) ? chosen : previous);
      setPublicationChoices(previous => ({ ...previous, [comparableKey(property)]: chosen }));
    } finally {
      if (epoch === publicationChoiceEpoch.current) setSelectionPending(false);
    }
  };
  const handleMarkerClick = useCallback((p) => {
    setSelectedProperty(p);
    if (p) {
      setListOpen(true);
      setVisitedProperties((prev) => prev.includes(p.id) ? prev : [...prev, p.id]);
    }
  }, []);
  const handlePropertyClick = useCallback((p) => {
    setSelectedProperty(p);
    setVisitedProperties((prev) => [...new Set([...prev, p.id])]);
  }, []);
  // Alternar los modos conserva el dibujo original y su ampliación.
  const handleSearchModeChange = useCallback((mode) => {
    if (mode !== 'radius' && mode !== 'polygons') return;
    setSearchMode(mode);
  }, []);

  // ---------- Loader bloqueante ----------
  const showBlockingLoader = booting || (enabled && !firstLoadDone);
  if (showBlockingLoader) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Inter,sans-serif' }}>
        Cargando tu sesión y resultados iniciales…
      </div>
    );
  }

  return (
    <div className="home-page">
      {showWizard && (
        <div className="wizard-modal">
          <PropertyWizard
            onComplete={handleWizardComplete}
            disabled={Boolean(sessionStorage.getItem('current_session'))}
          />
        </div>
      )}

      <Navbar
        filters={filters}
        setFilters={setFilters}
        searchMode={searchMode}
        onSearchModeChange={handleSearchModeChange}
        polygonsCount={polygonsPaths.length}
        polygonExpansion={polygonExpansion}
        onPolygonExpansionChange={setPolygonExpansion}
        filtersRequest={filtersRequest}
      />

      <main className={`home-workspace${listOpen ? "" : " home-workspace--list-collapsed"}${selectedProperty ? " home-workspace--property-selected" : ""}`}>
        <section className="home-map" aria-label="Mapa de propiedades">
          <Map
            displayedProperties={displayedProps}
            onMarkerClick={handleMarkerClick}
            selectedProperty={selectedProperty}
            visitedProperties={visitedProperties}
            selectedPlace={selectedPlace}
            center={filters.center}
            radius={filters.radius}
            searchMode={searchMode}
            polygonsPaths={expandedZone.paths}
            originalPolygonsPaths={polygonExpansion > 0 && !expandedZone.error ? polygonsPaths : []}
            listOpen={listOpen}
          />
          {fewResults && (
            <SearchExpansionNotice
              count={properties.length}
              loading={loading || refreshing}
              dismissed={widenDismissed}
              onDismiss={() => setWidenDismissed(true)}
              onOpenFilters={() => setFiltersRequest((current) => current + 1)}
            />
          )}
          {!listOpen && <button
            type="button"
            className="home-properties-reopen"
            onClick={() => setListOpen(true)}
            aria-controls="comparable-properties"
            aria-expanded={false}
          >
            <i className="pi pi-angle-left" aria-hidden="true" />
            <span>Propiedades</span>
            <strong>{loading ? "…" : properties.length}</strong>
          </button>}

        </section>
        <aside id="comparable-properties" className={`home-properties${refreshing ? " home-properties--refreshing" : ""}`} aria-label="Lista de propiedades" aria-hidden={!listOpen} inert={listOpen ? undefined : ""}>
          <div className="home-properties__sheet">
          <div className="home-properties__heading">
            <h1>Propiedades comparables</h1>
            <div className="home-properties__heading-actions">
              <span>{loading ? "Buscando…" : refreshing ? "Actualizando…" : `${properties.length} resultados`}</span>
              <button
                type="button"
                className="home-properties__collapse"
                aria-label="Ocultar propiedades comparables"
                onClick={() => setListOpen(false)}
              >
                <span>Ocultar</span>
                <i className="pi pi-angle-right" aria-hidden="true" />
              </button>
            </div>
          </div>
          {searchMode === 'polygons' && expandedZone.error && (
            <div className="home-zone-error" role="alert">{expandedZone.error}</div>
          )}
          <div className="home-properties__content">
            {savedComparables !== null && <div className="publication-status">
              <strong>Comparables guardados</strong>
              <p>{checkingAvailability ? 'Consultando disponibilidad actual…' : 'Se mantienen los anuncios y precios usados en este informe. Los retirados se indican en cada ficha.'}</p>
              <button type="button" onClick={() => { snapshotRequest.current += 1; publicationChoiceEpoch.current += 1; setPublicationChoices({}); setSelectionPending(false); setCheckingAvailability(false); setSavedComparables(null); setInactiveProperties([]); seenPropertyIdsRef.current = new Set(); }}>Buscar comparables actuales</button>
            </div>}
            <PropertyList
              properties={displayedProps}
              loading={loading}
              refreshing={refreshing}
              error={error}
              selectedProperty={selectedProperty}
              listOpen={listOpen}
              onPropertyClick={handlePropertyClick}
              visitedProperties={visitedProperties}
              inactiveProperties={inactiveProperties}
              onSelectPublication={savedComparables === null ? handleSelectPublication : undefined}
              selectionPending={selectionPending}
              onToggleInactive={handleToggleInactive}
              wizardData={wizardData}
              hasZone={hasGeoArea}
            />
          </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
